/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {PublicRoutes} from './PublicRoutes'
import {CleanerRoutes} from './CleanerRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, useAuth} from '../modules/auth'
import {Role} from '../types/domain'
import {App} from '../App'
import {FlyerLivePage} from '../pages/public/FlyerLivePage'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {/* Public shareable flyer — reachable by every role (guest, renter, admin). */}
          <Route path='flyer/live' element={<FlyerLivePage />} />
          {currentUser?.appRole === Role.ADMIN ? (
            <>
              {/* Admins get the Metronic admin panel. */}
              <Route path='/*' element={<PrivateRoutes />} />
            </>
          ) : currentUser?.appRole === Role.CLEANER ? (
            <>
              {/* Housekeeping (Vanda) — a minimal bed-wash + door-codes view only. */}
              <Route path='/*' element={<CleanerRoutes />} />
            </>
          ) : (
            <>
              {/* Everyone else — guests and signed-in renters — shares the
                  public layout. Renters additionally get their dashboard pages;
                  PublicRoutes branches on auth state. */}
              <Route path='/*' element={<PublicRoutes />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
